import React from 'react';
import { motion } from 'framer-motion';
import { FaFileContract, FaBalanceScale, FaHandshake, FaExclamationTriangle, FaEnvelope } from 'react-icons/fa';

const TermsOfService = () => {
  const sections = [
    {
      icon: <FaHandshake />,
      title: "Acceptance of Terms",
      content: [
        "By accessing and using Worga Naturals' website and services, you accept and agree to be bound by these Terms of Service.",
        "If you do not agree to these terms, please do not use our services.",
        "We reserve the right to modify these terms at any time. Continued use constitutes acceptance of modified terms.",
        "It is your responsibility to review these terms periodically."
      ]
    },
    {
      icon: <FaBalanceScale />,
      title: "Use of Services",
      content: [
        "You must be at least 18 years old to use our services",
        "You agree to provide accurate and complete information",
        "You are responsible for maintaining the confidentiality of your account",
        "You agree not to use our services for any unlawful purpose",
        "You will not interfere with or disrupt our services",
        "Commercial use requires prior written permission"
      ]
    },
    {
      icon: <FaFileContract />,
      title: "Product Information & Orders",
      content: [
        "We strive to provide accurate product descriptions and pricing",
        "Product availability is subject to change without notice",
        "We reserve the right to refuse or cancel any order",
        "Prices are subject to change without prior notice",
        "All orders are subject to acceptance and availability",
        "Bulk orders may require additional terms and conditions"
      ]
    },
    {
      icon: <FaExclamationTriangle />,
      title: "Intellectual Property",
      content: [
        "All content on this website is owned by Worga Naturals",
        "You may not reproduce, distribute, or create derivative works without permission",
        "Trademarks, logos, and brand names are protected",
        "Unauthorized use may result in legal action",
        "User-generated content remains your property but grants us usage rights"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-dark-900 dark:to-dark-800">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-700 to-primary-800 dark:from-primary-900 dark:to-primary-950 text-white py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <FaFileContract className="text-6xl mx-auto mb-6 text-primary-200" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Please read these terms carefully before using our services. By using our website, you agree to these terms and conditions.
            </p>
            <p className="text-sm text-primary-200 mt-4">
              Effective Date: October 20, 2025
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12"
        >
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
            Welcome to <strong>Worga Naturals</strong>. These Terms of Service ("Terms") govern your use of our website and services. By accessing or using our services, you agree to comply with and be bound by these Terms.
          </p>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            These Terms apply to all visitors, users, and others who access or use our services. Please read them carefully and contact us if you have any questions.
          </p>
        </motion.div>

        {/* Terms Sections */}
        <div className="space-y-12">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 * index }}
              className="bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-dark-700"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="text-3xl text-primary-600 dark:text-primary-400">
                  {section.icon}
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {section.title}
                </h2>
              </div>
              <ul className="space-y-3">
                {section.content.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <span className="text-primary-600 dark:text-primary-400 mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Additional Sections */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 space-y-8"
        >
          {/* Limitation of Liability */}
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-8 border border-amber-200 dark:border-amber-800">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Limitation of Liability
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              To the maximum extent permitted by law, Worga Naturals shall not be liable for:
            </p>
            <ul className="space-y-2 text-gray-700 dark:text-gray-300">
              <li className="flex items-start gap-3">
                <span className="text-amber-600 dark:text-amber-400">⚠</span>
                <span>Any indirect, incidental, special, or consequential damages</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 dark:text-amber-400">⚠</span>
                <span>Loss of profits, data, or business opportunities</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-600 dark:text-amber-400">⚠</span>
                <span>Damages resulting from unauthorized access or use of our services</span>
              </li>
            </ul>
          </div>

          {/* Governing Law */}
          <div className="bg-gray-50 dark:bg-dark-800 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Governing Law & Dispute Resolution
            </h2>
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p className="leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of Sri Lanka, without regard to its conflict of law provisions.
              </p>
              <p className="leading-relaxed">
                Any disputes arising from these Terms or your use of our services shall be resolved through binding arbitration in Colombo, Sri Lanka.
              </p>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-800 dark:to-primary-900 rounded-2xl p-8 text-white text-center">
            <FaEnvelope className="text-5xl mx-auto mb-4 text-primary-200" />
            <h2 className="text-2xl font-bold mb-4">Questions About Our Terms?</h2>
            <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
              If you have any questions about these Terms of Service, please contact our legal team.
            </p>
            <a
              href="mailto:legal@worganaturals.com"
              className="inline-flex items-center gap-2 bg-white text-primary-700 px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
            >
              <FaEnvelope />
              Contact Legal Team
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;
